/**
 * What each Défi tile lets you configure (V6 · V23).
 *
 * ⚠ THE OPTION SET IS PER TOOL, THE WIDGET IS NOT. One tile may open an existing
 * panel (the notion picker, the exam builder, the station picker); every other
 * tile declares plain fields here and `TileConfigPanel` renders them — one
 * style, one wording, one component. A tile that draws its own panel is a bug.
 *
 * A tile with nothing of its own still opens the widget, never an empty box:
 * the shared row (chrono · son, plus the run length where it applies) is always
 * there.
 */

import { boardDefaults, boardQuery } from '../calcul/boardConfigSchema';
import { LAUNCH_SOURCE } from '../calcul/calculConstants';
import { isLyceeClass } from '../../utils/classLevelUtils';

/** Existing panels a tile delegates to instead of declaring fields. */
export const TILE_PANEL = Object.freeze({
  NOTION: 'notion',
  EXAM: 'exam',
  STATION: 'station',
});

export const TILE_FIELD = Object.freeze({
  DOOR: 'door',
  MODE: 'mode',
  NUMBER_KIND: 'numberKind',
  CLASS: 'class',
  SESSION: 'session',
  LENGTH: 'length',
});

/**
 * Tiles whose option set is the SHARED board schema (RVW2 step 6).
 *
 * ⚠ These tiles declare no fields of their own. `boardConfigSchema.js` owns
 * them, `BoardConfigFields` renders them, and the server validates them —
 * one table for the station and for Défi Express, never two.
 */
export const SCHEMA_TILES = Object.freeze({
  'nombres-en-lettres': 'nombres-en-lettres',
  english: 'english',
  code: 'code',
});

/** Scratch / Python opens two doors (V20). */
export const CODE_DOOR = Object.freeze({ QUESTIONS: 'questions', WORKSHOP: 'workshop' });

export const CODE_LANGUAGE = Object.freeze({ SCRATCH: 'scratch', PYTHON: 'python' });

/** Session defaults: the choice is recreated whenever DÃ©fi Express opens. */
export function initialTileConfigForClass(classLevel) {
  const language = isLyceeClass(classLevel) ? CODE_LANGUAGE.PYTHON : CODE_LANGUAGE.SCRATCH;
  return {
    ...DEFAULT_TILE_CONFIG,
    ...boardDefaults('code'),
    language,
    door: language === CODE_LANGUAGE.PYTHON ? CODE_DOOR.QUESTIONS : CODE_DOOR.WORKSHOP,
  };
}

/**
 * The workshop is a block editor. There is no Python workshop, so the door
 * resolves per language and `python` resolves to nothing rather than to the
 * Scratch atelier (RVW3 C1).
 */
export const WORKSHOP_HREF = Object.freeze({
  [CODE_LANGUAGE.SCRATCH]: '/calcul/scratch',
  [CODE_LANGUAGE.PYTHON]: null,
});

/**
 * `shared` says which of the always-shared controls this tile carries.
 * An exam-like tile keeps `sound` only: its duration is set by the paper, so a
 * settable chrono would contradict it.
 */
export const SHARED = Object.freeze({
  FULL: Object.freeze(['chrono', 'sound']),
  SOUND_ONLY: Object.freeze(['sound']),
  NONE: Object.freeze([]),
});

const tile = (config) => Object.freeze({
  panel: null, fields: Object.freeze([]), schema: null, ...config,
});

export const TILE_CONFIG = Object.freeze({
  maths: tile({ panel: TILE_PANEL.NOTION, shared: SHARED.FULL }),
  exam: tile({ panel: TILE_PANEL.EXAM, shared: SHARED.SOUND_ONLY }),
  calcul: tile({ panel: TILE_PANEL.STATION, shared: SHARED.FULL }),
  station: tile({ panel: TILE_PANEL.STATION, shared: SHARED.FULL }),
  // Language + door, then the question kind and level behind the questions
  // door — all from the shared schema (V20).
  code: tile({ schema: SCHEMA_TILES.code, shared: SHARED.SOUND_ONLY }),
  // Direction, kind and the bounds; the run length is the question count.
  'nombres-en-lettres': tile({
    schema: SCHEMA_TILES['nombres-en-lettres'],
    fields: Object.freeze([TILE_FIELD.LENGTH]),
    shared: SHARED.SOUND_ONLY,
  }),
  niveau: tile({ fields: Object.freeze([TILE_FIELD.CLASS]), shared: SHARED.SOUND_ONLY }),
  diplome: tile({ fields: Object.freeze([TILE_FIELD.SESSION]), shared: SHARED.SOUND_ONLY }),
  sport: tile({ fields: Object.freeze([TILE_FIELD.LENGTH]), shared: SHARED.SOUND_ONLY }),
  mental: tile({ fields: Object.freeze([TILE_FIELD.LENGTH]), shared: SHARED.SOUND_ONLY }),
  // By skill and level (V21). The bank carries two tags and three levels, so
  // those are the choices — no invented taxonomy, no grammar-rule dropdown
  // with no data behind it.
  english: tile({
    schema: SCHEMA_TILES.english,
    fields: Object.freeze([TILE_FIELD.LENGTH]),
    shared: SHARED.SOUND_ONLY,
  }),
  autre: tile({ fields: Object.freeze([TILE_FIELD.LENGTH]), shared: SHARED.SOUND_ONLY }),
});

/**
 * The board a tile opens when it navigates rather than plays in place.
 *
 * Declared here so the configuration and the destination live together: a
 * configured option becomes a query parameter on exactly this link.
 */
export const TILE_HREF = Object.freeze({
  'nombres-en-lettres': '/calcul/nombres-en-lettres',
  // The workshop door now has a workshop to open: the rebuilt editor, drawn
  // with the same blocks as the questions door (SCR2). The questions door
  // still plays in place — `tileLaunch` sends only the workshop here.
  code: '/calcul/scratch',
});

export const RUN_LENGTHS = Object.freeze([3, 5, 10]);
export const DIPLOMA_SESSIONS = Object.freeze(['full', 'simulation_30', 'chapter_review']);

export const DEFAULT_TILE_CONFIG = Object.freeze({
  [TILE_FIELD.CLASS]: null,
  [TILE_FIELD.SESSION]: DIPLOMA_SESSIONS[0],
  [TILE_FIELD.LENGTH]: RUN_LENGTHS[0],
  sound: true,
  chrono: false,
});

/**
 * The choices each field offers, labelled from the caller's copy.
 *
 * `codeQuestionsReady` is false until the code bank is ingested (V7): the door
 * is still offered, greyed, with the reason on the chip — never hidden (V20).
 */
export function tileChoices(copy, {
  classes = [], codeQuestionsReady = false, language = CODE_LANGUAGE.SCRATCH,
} = {}) {
  const plain = (values, label) => values.map((value) => ({ value, label: label(value) }));
  const pythonPicked = language === CODE_LANGUAGE.PYTHON;
  return {
    // The door is a schema field now; only its availability is a tile concern.
    [TILE_FIELD.DOOR]: [
      {
        value: CODE_DOOR.QUESTIONS,
        label: copy.doors.questions,
        unavailable: codeQuestionsReady ? null : copy.doorSoon,
      },
      {
        value: CODE_DOOR.WORKSHOP,
        label: copy.doors.workshop,
        // Greyed with its reason, never hidden (V20): Python has questions only.
        unavailable: pythonPicked ? copy.doors.workshopScratchOnly : null,
      },
    ],
    [TILE_FIELD.CLASS]: plain(classes, (value) => value),
    [TILE_FIELD.SESSION]: plain(DIPLOMA_SESSIONS, (mode) => copy.sessions?.[mode] ?? mode),
    [TILE_FIELD.LENGTH]: plain(RUN_LENGTHS, (count) => String(count)),
  };
}

export const configOf = (intent) => TILE_CONFIG[intent] || null;

export function initialTileConfigsForClass(classLevel) {
  return Object.fromEntries(Object.entries(TILE_CONFIG).map(([intent, config]) => [intent, {
    ...DEFAULT_TILE_CONFIG,
    ...boardDefaults(config.schema),
    ...(intent === 'code' ? initialTileConfigForClass(classLevel) : {}),
  }]));
}

/**
 * What a configured tile actually launches — the one contract between the
 * panel and the run (RVW1 step 6).
 *
 * ⚠ AN OPTION THAT REACHES NOTHING IS DECORATION. Every field declared above
 * is consumed here: `length` sizes the run, `door` chooses which Scratch/Python
 * door opens, and the rest become query parameters on the board the tile opens.
 * Adding a field without a case here is the bug this function exists to make
 * visible.
 *
 * Returns `{ href, run, door }` — `href` when the tile navigates, `run` when it
 * plays in place, `door` for the code tile's two doors.
 */
export function tileLaunch(intent, values = {}, { href = null } = {}) {
  const config = configOf(intent);
  const base = href || TILE_HREF[intent] || null;
  if (!config) return { href: base, run: null, door: null };
  const settings = { ...DEFAULT_TILE_CONFIG, ...boardDefaults(config.schema), ...values };
  const query = new URLSearchParams();

  // A schema tile's options travel as the server's own query keys, so the
  // board it opens is configured by the same contract the server validates.
  if (config.schema) {
    new URLSearchParams(boardQuery(config.schema, settings))
      .forEach((value, key) => query.set(key, value));
  }
  if (config.fields.includes(TILE_FIELD.SESSION)) {
    query.set('session', settings[TILE_FIELD.SESSION]);
  }
  if (config.fields.includes(TILE_FIELD.CLASS) && settings[TILE_FIELD.CLASS]) {
    query.set('classe', settings[TILE_FIELD.CLASS]);
  }
  // The shared run length is the only field that sizes a run in place.
  const run = config.fields.includes(TILE_FIELD.LENGTH)
    ? { total: Number(settings[TILE_FIELD.LENGTH]) }
    : null;
  // The code tile's door now lives in the shared schema.
  const isCode = config.schema === SCHEMA_TILES.code;
  const door = isCode ? settings.door : null;
  // The two code doors are two different acts: the workshop is a board to open,
  // the questions are a run to play here. Only the workshop navigates, and only
  // for a language that has a workshop — Python has none.
  let target = base;
  if (isCode) {
    const hasWorkshop = Boolean(WORKSHOP_HREF[settings.language]);
    target = door === CODE_DOOR.WORKSHOP && hasWorkshop ? base : null;
    if (target) query.set(LAUNCH_SOURCE.PARAM, LAUNCH_SOURCE.DEFI);
  }
  const search = query.toString();
  return {
    href: target ? (search ? `${target}${target.includes('?') ? '&' : '?'}${search}` : target) : null,
    run,
    door,
    questionFilters: isCode ? { language: settings.language, level: settings.level, kind: settings.kind } : null,
    sound: Boolean(settings.sound),
    chrono: Boolean(settings.chrono),
  };
}

/** Every tile opens something — never an empty panel with a title (V23). */
export const hasConfig = (intent) => {
  const config = configOf(intent);
  return Boolean(config) && Boolean(config.panel || config.fields.length || config.shared.length);
};
